import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "onChange", "size", "name", "mode", "isIconOnly", "children", "defaultValue", "value"],
    _excluded2 = ["className", "id", "name", "icon", "isIconOnly", "value", "aria-label", "aria-labelledby", "isChecked", "isDisabled", "isInvalid", "defaultIsChecked", "onChange", "onBlur", "onFocus", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { Fragment, useRef, useState, cloneElement, forwardRef, useImperativeHandle } from 'react';
import { ThemeProvider } from 'styled-components';
import cx from 'classnames';
import { prefix } from '../../tools/settings';
import { useId } from '../../hooks';
import { cleanChildren } from '../../tools/functions';
import { Styled } from './SegmentedControl.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var SegmentedControl = forwardRef(function (_ref, ref) {
  var _cx;

  var customClassName = _ref.className,
      onChange = _ref.onChange,
      size = _ref.size,
      name = _ref.name,
      mode = _ref.mode,
      _ref$isIconOnly = _ref.isIconOnly,
      isIconOnly = _ref$isIconOnly === void 0 ? false : _ref$isIconOnly,
      children = _ref.children,
      defaultValue = _ref.defaultValue,
      valueProp = _ref.value,
      rest = _objectWithoutProperties(_ref, _excluded);

  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "-segmented-control"), true), _defineProperty(_cx, "".concat(prefix, "-segmented-control-icon"), isIconOnly), _defineProperty(_cx, customClassName, !!customClassName), _cx));

  var _useRef = useRef(valueProp !== null),
      isControlled = _useRef.current;

  var _useState = useState(defaultValue || null),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  var _value = isControlled ? valueProp : value;

  var rootRef = useRef();

  var _onChange = function _onChange(event) {
    if (!isControlled) {
      setValue(event.target.value);
    }

    if (onChange) {
      onChange(event, event.target.value);
    }
  };

  var fallbackName = useId('segment');

  var _name = name || fallbackName;

  var validChildren = cleanChildren(children);
  var validChildrenLength = validChildren.length;
  var clones = validChildren.map(function (child, index) {
    return _jsx(Fragment, {
      children: cloneElement(child, {
        name: _name,
        onChange: _onChange,
        isChecked: child.props.value === _value,
        id: "".concat(_name, "-").concat(index),
        isIconOnly: isIconOnly
      })
    }, index);
  });
  useImperativeHandle(ref, function () {
    return {
      focus: function focus() {
        var input = rootRef.current.querySelector('input:not(:disabled):checked');

        if (!input) {
          input = rootRef.current.querySelector('input:not(:disabled)');
        }

        if (input) {
          input.focus();
        }
      }
    };
  }, []);
  return _jsx(ThemeProvider, {
    theme: {
      mode: mode,
      size: size
    },
    children: _jsx(Styled.SegmentedControl, _objectSpread(_objectSpread({
      ref: rootRef,
      role: "radiogroup",
      className: className,
      length: validChildrenLength
    }, rest), {}, {
      children: clones
    }))
  });
});
var SegmentedControlButton = forwardRef(function (_ref2, ref) {
  var _cx2;

  var customClassName = _ref2.className,
      id = _ref2.id,
      name = _ref2.name,
      icon = _ref2.icon,
      isIconOnly = _ref2.isIconOnly,
      value = _ref2.value,
      ariaLabel = _ref2['aria-label'],
      ariaLabelledBy = _ref2['aria-labelledby'],
      isChecked = _ref2.isChecked,
      isDisabled = _ref2.isDisabled,
      isInvalid = _ref2.isInvalid,
      defaultIsChecked = _ref2.defaultIsChecked,
      onChange = _ref2.onChange,
      onBlur = _ref2.onBlur,
      onFocus = _ref2.onFocus,
      children = _ref2.children,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var className = cx((_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "-segmented-control-button"), true), _defineProperty(_cx2, customClassName, !!customClassName), _cx2));
  return _jsxs(Fragment, {
    children: [_jsx("input", {
      className: className,
      type: "radio",
      id: id,
      value: value,
      ref: ref,
      name: name,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "data-invalid": isInvalid,
      onChange: onChange,
      onBlur: onBlur,
      onFocus: onFocus,
      checked: isChecked,
      disabled: isDisabled
    }), _jsx("label", {
      htmlFor: id,
      children: isIconOnly ? _jsx(Styled.SegmentedControlIcon, {
        className: "icon",
        icon: icon
      }) : _jsxs(_Fragment, {
        children: [icon && _jsx(Styled.SegmentedControlIcon, {
          className: "icon",
          icon: icon
        }), _jsx("span", {
          className: "text",
          children: children
        })]
      })
    })]
  });
});
SegmentedControl.Button = SegmentedControlButton;
SegmentedControl.displayName = 'SegmentedControl';
export default SegmentedControl;